import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Link,
  Alert,
  CircularProgress,
  Paper,
  InputAdornment,
  IconButton,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Email,
  Lock,
  Login as LoginIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import ModernCard from './common/ModernCard';
import ModernButton from './common/ModernButton';
import axiosInstance from './axiosConfig';
import { setToken, setRefreshToken } from './authStorage';
import { useLanguage } from '../contexts/LanguageContext';

const LoginContainer = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(2),
  background: 'transparent',
}));

const LoginCard = styled(ModernCard)(({ theme }) => ({
  width: '100%',
  maxWidth: 450,
  padding: theme.spacing(4),
  textAlign: 'center',
  position: 'relative',
  overflow: 'hidden',
  
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '4px',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)',
  },
}));

const Logo = styled(Typography)(({ theme }) => ({
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
  fontWeight: 700,
  fontSize: '2.5rem',
  letterSpacing: '-0.02em',
  marginBottom: theme.spacing(1),
  color: theme.palette.primary.main, // Fallback color
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: theme.shape.borderRadius * 2,
    background: theme.palette.mode === 'dark' 
      ? 'rgba(255, 255, 255, 0.05)' 
      : 'rgba(255, 255, 255, 0.8)',
    backdropFilter: 'blur(10px)',
    border: `1px solid ${theme.palette.mode === 'dark' 
      ? 'rgba(255, 255, 255, 0.1)' 
      : 'rgba(255, 255, 255, 0.3)'}`,
    transition: 'all 0.3s ease',
    
    '&:hover': {
      background: theme.palette.mode === 'dark' 
        ? 'rgba(255, 255, 255, 0.08)' 
        : 'rgba(255, 255, 255, 0.9)',
      borderColor: theme.palette.primary.main,
    },
    
    '&.Mui-focused': {
      background: theme.palette.mode === 'dark' 
        ? 'rgba(255, 255, 255, 0.1)' 
        : 'rgba(255, 255, 255, 1)',
      borderColor: theme.palette.primary.main,
      boxShadow: `0 0 0 2px ${theme.palette.primary.main}20`,
    },
  },
  
  '& .MuiInputLabel-root': {
    color: theme.palette.text.secondary,
    fontWeight: 500,
  },
  
  '& .MuiInputLabel-root.Mui-focused': {
    color: theme.palette.primary.main,
    fontWeight: 600,
  },

  '& .MuiInputBase-input': {
    color: theme.palette.text.primary,
    '&::placeholder': {
      color: theme.palette.text.secondary,
      opacity: 1,
    },
  },
}));

const Login = () => {
  const { translate } = useLanguage();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Debug temporal - remover después
      console.log('🔍 Debug Login:');
      console.log('   axiosInstance.defaults.baseURL:', axiosInstance.defaults.baseURL);
      console.log('   URL completa:', axiosInstance.defaults.baseURL + '/api/login');
      
      const response = await axiosInstance.post('/api/login', formData);
      
      // Verificar que la respuesta tenga la estructura correcta de la nueva API
      if (response.data && response.data.message === 'Login exitoso' && response.data.accessToken && response.data.refreshToken) {
        // Guardar tokens usando las funciones de almacenamiento apropiadas
        await setToken(response.data.accessToken);
        await setRefreshToken(response.data.refreshToken);
        
        // Decodificar datos del usuario desde el token JWT
        try {
          const tokenPayload = JSON.parse(atob(response.data.accessToken.split('.')[1]));
          const userData = {
            id: tokenPayload.id || tokenPayload.userId,
            email: tokenPayload.email,
            role: tokenPayload.role,
            name: tokenPayload.name || tokenPayload.email.split('@')[0]
          };
          
          // Guardar datos de usuario en localStorage para compatibilidad
          localStorage.setItem('user', JSON.stringify(userData));
          localStorage.setItem('userId', userData.id);
        } catch (tokenError) {
          console.error('Error decoding token:', tokenError);
          // Si no se puede decodificar el token, usar datos por defecto
          const userData = {
            id: 'unknown',
            email: formData.email,
            role: 'user',
            name: formData.email.split('@')[0]
          };
          localStorage.setItem('user', JSON.stringify(userData));
          localStorage.setItem('userId', userData.id);
        }
        
        navigate('/dashboard-overview');
      } else {
        // La respuesta no tiene la estructura esperada
        setError(response.data?.message || translate('login.invalidServerResponse'));
      }
    } catch (err) {
      console.error('Login error:', err);
      if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else if (err.response?.status === 401) {
        setError(translate('login.invalidCredentials'));
      } else if (err.response?.status >= 500) {
        setError(translate('login.serverError'));
      } else {
        setError(translate('login.connectionError'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <LoginContainer>
      <LoginCard variant="glass">
        <Box sx={{ mb: 4 }}>
          <Logo variant="h3" component="h1">
            {translate('login.welcome')}
          </Logo>
          <Typography 
            variant="body1" 
            color="text.secondary"
            sx={{ 
              color: 'text.secondary',
              fontWeight: 500,
            }}
          >
            {translate('login.signInToAccount')}
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
          <StyledTextField
            fullWidth
            label={translate('login.email')}
            name="email"
            type="email"
            value={formData.email}
            onChange={handleInputChange}
            required
            margin="normal"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Email color="action" />
                </InputAdornment>
              ),
            }}
          />

          <StyledTextField
            fullWidth
            label={translate('login.password')}
            name="password"
            type={showPassword ? 'text' : 'password'}
            value={formData.password}
            onChange={handleInputChange}
            required
            margin="normal"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Lock color="action" />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={handleTogglePasswordVisibility}
                    edge="end"
                    size="small"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <ModernButton
            type="submit"
            fullWidth
            variant="contained"
            size="large"
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <LoginIcon />}
            disabled={loading}
            sx={{ mt: 3, mb: 2, py: 1.5 }}
          >
            {loading ? translate('login.signingIn') : translate('login.signIn')}
          </ModernButton>

          <Box sx={{ mt: 3, textAlign: 'center' }}>
            <Typography 
              variant="body2" 
              color="text.secondary"
              sx={{ 
                color: 'text.secondary',
                fontWeight: 500,
              }}
            >
              {translate('login.noAccount')}{' '}
              <Link
                component={RouterLink}
                to="/register"
                sx={{
                  color: 'primary.main',
                  textDecoration: 'none',
                  fontWeight: 600,
                  '&:hover': {
                    textDecoration: 'underline',
                  },
                }}
              >
                {translate('login.registerHere')}
              </Link>
            </Typography>
          </Box>
        </Box>
      </LoginCard>
    </LoginContainer>
  );
};

export default Login;
